/**
 * Subscription billing contract (F24): plans, the workspace's billing state,
 * Stripe Checkout and Customer Portal hand-offs, and the error codes that
 * entitlement checks return. Re-exported from index.ts.
 *
 * The plan catalogue lives here so the pricing page and the Worker's
 * entitlement checks read the same numbers.
 *
 * Owner: billing (F24). Implements BILL-010, BILL-030, BILL-040, BILL-050,
 * BILL-060, BILL-090, BILL-091, BILL-094 and BILL-095.
 */

export const BILLING_ROUTES = {
  /** GET -> 200 BillingResponse for the caller's personal workspace (E120; BILL-050). */
  billing: '/api/v1/billing',
  /** POST CheckoutRequest -> 200 CheckoutResponse (E121; BILL-030). Workspace owner only. */
  billingCheckout: '/api/v1/billing/checkout',
  /** POST -> 200 PortalResponse (E122; BILL-040). Workspace owner only. */
  billingPortal: '/api/v1/billing/portal',
  /** POST, raw Stripe event body with a Stripe-Signature header (E123; BILL-070). Called by Stripe only. */
  stripeWebhook: '/api/stripe/webhook',
  /** POST ConfirmCheckoutRequest -> 200 BillingResponse (E129; BILL-067). Workspace owner only. */
  billingConfirm: '/api/v1/billing/confirm',
  /** POST QuantityRequest -> 200 BillingResponse (E124; BILL-095). Workspace owner, Review plan only. */
  billingQuantity: '/api/v1/billing/quantity',
  /** GET -> 200 ReviewSlotsResponse for the caller's personal workspace (E125; BILL-094). */
  reviews: '/api/v1/reviews',
  /** GET -> 200 ReviewStateResponse for a page the caller may read, else 404 (E126; BILL-094). */
  reviewState: (slug: string) => `/api/v1/reviews/page/${encodeURIComponent(slug)}`,
  /** POST -> 200 ReviewArchiveResponse (E127; BILL-090). Workspace owner only. */
  reviewArchive: (questionId: string) => `/api/v1/reviews/${encodeURIComponent(questionId)}/archive`,
  /** POST -> 200 ReviewArchiveResponse, or 402 plan_limit_reached with no free slot (E128; BILL-091). Workspace owner only. */
  reviewReactivate: (questionId: string) => `/api/v1/reviews/${encodeURIComponent(questionId)}/reactivate`,
} as const;

export type BillingPlan = 'review' | 'lab';
export type BillingInterval = 'month' | 'year';

/** Stripe subscription statuses, as Stripe names them. */
export type SubscriptionStatus =
  | 'trialing'
  | 'active'
  | 'past_due'
  | 'canceled'
  | 'incomplete'
  | 'incomplete_expired'
  | 'unpaid'
  | 'paused';

/**
 * What the workspace may do now (BILL-080):
 * - `trial`: trialing subscription, full access with the trial screening cap;
 * - `paid`: active subscription;
 * - `grace`: past_due within the grace period, full access with a payment warning;
 * - `free`: no subscription, but the free plan is switched on (FREE_PLAN_ENABLED);
 * - `none`: read only (shared pages, library, existing data).
 */
export type BillingAccess = 'trial' | 'paid' | 'grace' | 'free' | 'none';

/** Plan numbers shown on the pricing page and enforced by the Worker (BILL-010). Prices in US dollars, tax exclusive. */
export const PLAN_CATALOGUE = {
  review: {
    name: 'Review',
    /** Price per active review. */
    monthUsd: 29,
    yearUsd: 290,
    perReview: true,
    /** AI-screened records a year, per paid review. */
    aiRecordsPerYear: 10_000,
  },
  lab: {
    name: 'Lab',
    monthUsd: 99,
    yearUsd: 990,
    perReview: false,
    maxActiveReviews: 5,
    aiRecordsPerYear: 50_000,
  },
} as const;

export const TRIAL_DAYS = 14;
/** AI-screened records a workspace may use during its free trial, whatever the plan (BILL-084). */
export const TRIAL_AI_RECORD_CAP = 2_000;
/** Days a past_due subscription keeps full access (BILL-082). */
export const PAST_DUE_GRACE_DAYS = 7;
/** The free plan, when FREE_PLAN_ENABLED is 'true' (BILL-086). */
export const FREE_PLAN = { maxActiveReviews: 1, aiRecordsPerYear: 1_000 } as const;
/** Everyone's allowance while pricing is paused (PRICING_PAUSED, BILL-130): the Review plan's, free. */
export const BETA_FREE_PLAN = { maxActiveReviews: 3, aiRecordsPerYear: 10_000 } as const;
export interface FreePlanLimits {
  maxActiveReviews: number;
  aiRecordsPerYear: number;
}
/**
 * Most review slots one Review subscription can have, at checkout, in the app and in the billing portal
 * (BILL-010, BILL-030, BILL-041, BILL-095). Four Review slots would cost more than Lab, so from 4 the
 * app points to Lab instead (BILL-112).
 */
export const REVIEW_MAX_SLOTS = 3;
/** @deprecated Use REVIEW_MAX_SLOTS; kept so older imports still compile. */
export const MAX_REVIEW_QUANTITY = REVIEW_MAX_SLOTS;
/** Institution and guideline-group plans are arranged by email, not checkout (BILL-011). */
export const SALES_EMAIL = 'support@bioterafi.xyz';

/** Stripe price lookup keys (BILL-020): the same in test and live mode, so no price IDs are configured. */
export const PRICE_LOOKUP_KEYS: Readonly<Record<BillingPlan, Readonly<Record<BillingInterval, string>>>> = {
  review: { month: 'review_month', year: 'review_year' },
  lab: { month: 'lab_month', year: 'lab_year' },
};

export interface BillingLimits {
  /** Questions in the workspace that are not archived (drafts included), against the most the plan allows (0 when access is `none`). BILL-083. */
  activeReviews: { used: number; max: number };
  aiScreenedRecords: {
    /** Records screened by AI in the current allowance window. */
    used: number;
    /** Records allowed in the window, with the trial cap already applied. */
    allowance: number;
    /** The allowance year (or trial) this count belongs to; null when access is `none`. */
    windowStart: string | null;
    windowEnd: string | null;
    /** TRIAL_AI_RECORD_CAP while trialing, else null. */
    trialCap: number | null;
  };
}

/** GET BILLING_ROUTES.billing. */
export interface BillingResponse {
  workspaceId: string;
  /** True when the caller owns the workspace, so may start checkout or open the portal. */
  canManage: boolean;
  access: BillingAccess;
  /** The plan paid for, 'free' when on the free plan, or null. */
  plan: BillingPlan | 'free' | null;
  status: SubscriptionStatus | null;
  interval: BillingInterval | null;
  /** Subscription quantity: paid reviews on the Review plan, 1 on Lab. */
  quantity: number | null;
  trialEndsAt: string | null;
  /** Whole days left in the trial, rounded up; null when not trialing. */
  trialDaysLeft: number | null;
  /** End of the current billing period: when the next charge is due, or when access ends if cancelling. */
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  /** True once this workspace has had a free trial; a second checkout has no trial (BILL-032). */
  trialUsed: boolean;
  /** True when the latest payment failed and has not been paid since. */
  paymentFailed: boolean;
  /** When `grace` access ends; null otherwise. */
  graceEndsAt: string | null;
  /** True when a Stripe customer exists, so the billing portal can open. */
  hasBillingAccount: boolean;
  /** False when the Worker has no Stripe key: checkout and portal answer 503. */
  checkoutAvailable: boolean;
  freePlanEnabled: boolean;
  /** BILL-130: pricing is paused; nothing can be bought and everyone without a subscription has BETA_FREE_PLAN. */
  pricingPaused: boolean;
  limits: BillingLimits;
}

/** POST BILLING_ROUTES.billingCheckout. `quantity` applies to the Review plan only: 1 to REVIEW_MAX_SLOTS, default 1 (the web app always sends 1). */
export interface CheckoutRequest {
  plan: BillingPlan;
  interval: BillingInterval;
  quantity?: number;
}

export interface CheckoutResponse {
  /** Stripe Checkout URL to send the browser to. */
  url: string;
  /** Whether this checkout includes the 14-day free trial (false after a first trial). */
  trial: boolean;
}

/**
 * POST BILLING_ROUTES.billingConfirm (BILL-067): the Checkout session Stripe returned the browser with
 * (`session_id` on /billing/success). Stripe's form: `cs_test_` or `cs_live_` then letters and digits.
 */
export interface ConfirmCheckoutRequest {
  sessionId: string;
}

/** POST BILLING_ROUTES.billingPortal. */
export interface PortalResponse {
  url: string;
}

/** POST BILLING_ROUTES.billingQuantity (BILL-095): the number of review slots on the Review plan, 1 to REVIEW_MAX_SLOTS. */
export interface QuantityRequest {
  quantity: number;
}

/** One review (a question and, once created, its evidence page) and whether it holds a slot (BILL-094). */
export interface ReviewSlot {
  questionId: string;
  title: string;
  questionStatus: 'draft' | 'protocol_frozen';
  /** The evidence page built from the question, or null while it has none. */
  pageSlug: string | null;
  /** When the review was archived, or null while it is active. */
  archivedAt: string | null;
  /** True when the review counts towards the plan's active reviews (it is not archived). */
  active: boolean;
}

/** GET BILLING_ROUTES.reviews. */
export interface ReviewSlotsResponse {
  /** Active reviews (BILL-083). */
  used: number;
  /** The most the plan allows now (0 without access). */
  max: number;
  /** True when the caller owns the workspace, so may archive and reactivate. */
  canManage: boolean;
  reviews: ReviewSlot[];
}

/** GET BILLING_ROUTES.reviewState(slug). */
export interface ReviewStateResponse {
  archived: boolean;
  archivedAt: string | null;
  /** The question behind the page, for members only; null for other readers and for imported pages. */
  questionId: string | null;
  /** True when the caller owns the page's workspace, so may archive or reactivate it. */
  canManage: boolean;
}

/** POST BILLING_ROUTES.reviewArchive / reviewReactivate. */
export interface ReviewArchiveResponse {
  review: ReviewSlot;
  used: number;
  max: number;
}

/**
 * ApiError codes for billing (BILL-090):
 * - 402 `subscription_required`: the action needs a trialing or active subscription (details: BillingRefusal);
 * - 402 `plan_limit_reached`: the plan's active-review or AI-record limit is reached (details: BillingRefusal);
 * - 409 `already_subscribed`: checkout while a subscription is live (use the portal);
 * - 409 `no_billing_account`: portal before any checkout;
 * - 503 `billing_unavailable`: Stripe is not configured or a price is missing;
 * - 502 `billing_error`: Stripe refused the request;
 * - 400 `invalid_signature`: a webhook whose Stripe-Signature does not verify;
 * - 409 `review_archived`: a write to an archived review, which is read-only (BILL-092);
 * - 409 `quantity_below_active_reviews`: fewer review slots than reviews in use (details: {used, requested}; BILL-095);
 * - 409 `quantity_above_maximum`: more Review slots than REVIEW_MAX_SLOTS, at checkout or in the app (details: {max, requested}; BILL-030, BILL-095);
 * - 409 `quantity_not_adjustable`: slots change only on a trialing or active Review subscription (BILL-095).
 */
export type BillingErrorCode =
  | 'subscription_required'
  | 'plan_limit_reached'
  | 'already_subscribed'
  | 'no_billing_account'
  | 'billing_unavailable'
  | 'billing_error'
  | 'invalid_signature'
  | 'review_archived'
  | 'quantity_below_active_reviews'
  | 'quantity_above_maximum'
  | 'quantity_not_adjustable';

/** ApiError.details on a 402. `action` is what the web app offers next. */
export interface BillingRefusal {
  reason: 'no_subscription' | 'subscription_ended' | 'payment_overdue' | 'active_reviews' | 'ai_records';
  action: 'start_trial' | 'resubscribe' | 'update_payment' | 'upgrade';
  access: BillingAccess;
  /** For plan limits: how many are used and the most allowed. */
  used?: number;
  max?: number;
}
